import React from 'react'
import { castStrengthToVerdict, getColorSchemaClass } from 'app/modules/reports/helpers'
import { SignalVerdict } from 'app/modules/reports/types'
import { Tooltip } from 'app/modules/reports/components/tooltip'
import { useVerdictLabel } from 'app/modules/reports/hooks/use-verdict-label'

type VerdictParams = {
  verdict?: SignalVerdict
  strength?: number
  style?: React.CSSProperties
  suffix?: JSX.Element | null
  className?: string
  content?: string | JSX.Element
  short?: boolean
  allowTooltip?: boolean
  variant?: 'primary' | 'secondary'
}

/**
 * Colored report/signal verdict badge.
 *
 * As verdict text it always uses `verdict`.
 * Color is derived either from `strength` or, if it is not provided, from `verdict`.
 */
export function VerdictBadge({
  verdict,
  className,
  strength,
  style,
  suffix,
  content,
  short = false,
  allowTooltip = true,
  variant = 'primary',
}: VerdictParams) {
  const realVerdict = strength ? castStrengthToVerdict(strength) : verdict
  const verdictLabel = useVerdictLabel(realVerdict)

  if (!verdictLabel) {
    return null
  }

  const verdictClass = getColorSchemaClass(realVerdict)
  const label = content ?? verdictLabel
  const text = short && typeof label === 'string' ? label[0] : label
  const secondaryVariant = variant === 'secondary'
  const containerClass = `badge badge-fs badge-fs-verdict${secondaryVariant ? '-secondary' : ''} ${
    className ?? ''
  } ${verdictClass} ${short ? 'short-badge' : ''}`
  const showTooltip = short && allowTooltip

  const badge = (
    <span style={style} className={containerClass}>
      <span className="m-auto">
        <span>{text}</span>
        {suffix || null}
      </span>
    </span>
  )

  return !showTooltip ? (
    badge
  ) : (
    <Tooltip id="verdict-tooltip" content={<span className="text-capital">{label}</span>} placement="top">
      {badge}
    </Tooltip>
  )
}

export function VerdictInterestingBadge({ verdict, label }: { verdict: SignalVerdict; label: string }) {
  return (
    <Tooltip id="verdict-tooltip" content={label} placement="top">
      <span className={`badge badge-fs badge-fs-verdict-icon badge-tag colors-${verdict}`}>
        <span className="m-auto">
          <i className={`ds3-icon ds3-warning-circle`} style={{ height: '20px' }}></i>
        </span>
      </span>
    </Tooltip>
  )
}
